import { useEffect, useMemo, useState } from "react";
import {
  approveRemoteWhatsAppGroupJoinRequests,
  checkRemoteWhatsAppGroupsEligibility,
  createRemoteWhatsAppGroup,
  getRemoteWhatsAppGroupInviteLink,
  listRemoteWhatsAppGroupJoinRequests,
  listRemoteWhatsAppGroups,
  rejectRemoteWhatsAppGroupJoinRequests,
  removeRemoteWhatsAppGroupParticipants,
  resetRemoteWhatsAppGroupInviteLink,
  sendRemoteWhatsAppGroupMessage,
  syncRemoteWhatsAppGroups,
  updateRemoteWhatsAppGroup,
  type GroupEligibility,
  type GroupJoinRequest,
  type WhatsAppGroup,
} from "../groupsApi";
import { Icon } from "../icons";
import type { AuthSession, WhatsAppConnection } from "../types";

type Notify = (tone: "success" | "warning" | "danger", text: string) => void;

type Props = {
  connections: WhatsAppConnection[];
  session: AuthSession;
  notify: Notify;
};

export function WhatsAppGroupsPanel({ connections, session, notify }: Props) {
  const activeConnections = useMemo(
    () => connections.filter((item) => item.status === "active"),
    [connections],
  );
  const [connectionId, setConnectionId] = useState("");
  const [groups, setGroups] = useState<WhatsAppGroup[]>([]);
  const [eligibility, setEligibility] = useState<GroupEligibility | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [joinRequests, setJoinRequests] = useState<GroupJoinRequest[]>([]);
  const [busy, setBusy] = useState("");
  const [createForm, setCreateForm] = useState({
    subject: "",
    description: "",
    joinApprovalMode: "approval_required" as "approval_required" | "auto_approve",
  });
  const [editForm, setEditForm] = useState({ subject: "", description: "" });
  const [messageBody, setMessageBody] = useState("");
  const [participantPhone, setParticipantPhone] = useState("");

  const selectedGroup = groups.find((item) => item.groupId === selectedGroupId);
  const selectedConnection = connections.find((item) => item.connectionId === connectionId);
  const eligible = eligibility?.eligible === true;

  useEffect(() => {
    const next = activeConnections.some((item) => item.connectionId === connectionId)
      ? connectionId
      : activeConnections[0]?.connectionId ?? "";
    if (next !== connectionId) setConnectionId(next);
  }, [activeConnections, connectionId]);

  useEffect(() => {
    setEligibility(null);
    setSelectedGroupId("");
    setInviteLink("");
    setJoinRequests([]);
    if (!connectionId) {
      setGroups([]);
      return;
    }
    let mounted = true;
    void listRemoteWhatsAppGroups(session, connectionId)
      .then((items) => {
        if (!mounted) return;
        setGroups(items);
        setSelectedGroupId((current) => current || items.find((item) => item.status !== "resolved")?.groupId || "");
      })
      .catch((cause) => mounted && notify("warning", `No se pudieron cargar los grupos guardados: ${errorMessage(cause)}`));
    return () => { mounted = false; };
  }, [connectionId, session]);

  useEffect(() => {
    setEditForm({
      subject: selectedGroup?.subject ?? "",
      description: selectedGroup?.description ?? "",
    });
    setInviteLink("");
    setJoinRequests([]);
    setMessageBody("");
    setParticipantPhone("");
  }, [selectedGroup?.groupId]);

  async function reloadGroups(preferredId?: string) {
    if (!connectionId) return;
    const items = await listRemoteWhatsAppGroups(session, connectionId);
    setGroups(items);
    const nextId = preferredId && items.some((item) => item.groupId === preferredId)
      ? preferredId
      : items.find((item) => item.status !== "resolved")?.groupId ?? "";
    setSelectedGroupId(nextId);
  }

  async function checkEligibility() {
    if (!connectionId) return;
    setBusy("eligibility");
    try {
      const result = await checkRemoteWhatsAppGroupsEligibility(connectionId, session);
      setEligibility(result);
      notify(
        result.eligible ? "success" : "warning",
        result.eligible
          ? "Meta confirmó que este número puede consultar la Groups API."
          : eligibilityMessage(result.reason),
      );
    } catch (cause) {
      notify("danger", `No se pudo comprobar la elegibilidad: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  async function syncGroups() {
    if (!connectionId) return;
    setBusy("sync");
    try {
      const result = await syncRemoteWhatsAppGroups(connectionId, session);
      setGroups(result.items);
      setEligibility({ eligible: true, connectionId });
      notify("success", `${result.synced} grupo${result.synced === 1 ? "" : "s"} sincronizado${result.synced === 1 ? "" : "s"} desde Meta.`);
    } catch (cause) {
      notify("danger", `No se pudieron sincronizar los grupos: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  async function createGroup() {
    if (!connectionId || !createForm.subject.trim()) return;
    setBusy("create");
    try {
      const created = await createRemoteWhatsAppGroup({
        connectionId,
        subject: createForm.subject.trim(),
        description: createForm.description.trim() || undefined,
        joinApprovalMode: createForm.joinApprovalMode,
      }, session);
      setCreateForm({ subject: "", description: "", joinApprovalMode: "approval_required" });
      await reloadGroups(created.groupId);
      notify(
        "success",
        created.status === "creating"
          ? "Meta recibió la creación. El grupo aparecerá como activo cuando llegue el evento de confirmación."
          : "Grupo creado y vinculado a esta organización.",
      );
    } catch (cause) {
      notify("danger", `No se pudo crear el grupo: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  async function saveGroup() {
    if (!selectedGroup || !editForm.subject.trim()) return;
    setBusy("edit");
    try {
      await updateRemoteWhatsAppGroup(selectedGroup.groupId, {
        subject: editForm.subject.trim(),
        description: editForm.description.trim(),
      }, session);
      await reloadGroups(selectedGroup.groupId);
      notify("success", "Nombre y descripción actualizados en WhatsApp.");
    } catch (cause) {
      notify("danger", `No se pudo editar el grupo: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  async function loadInviteLink(reset = false) {
    if (!selectedGroup) return;
    setBusy(reset ? "reset-link" : "link");
    try {
      const result = reset
        ? await resetRemoteWhatsAppGroupInviteLink(selectedGroup.groupId, session)
        : await getRemoteWhatsAppGroupInviteLink(selectedGroup.groupId, session);
      setInviteLink(result.inviteLink);
      notify("success", reset ? "Enlace anterior invalidado y reemplazado." : "Enlace de invitación recuperado.");
    } catch (cause) {
      notify("danger", `No se pudo obtener el enlace: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  async function copyInviteLink() {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      notify("success", "Enlace copiado.");
    } catch {
      notify("warning", "No se pudo copiar automáticamente. Selecciona el enlace y cópialo manualmente.");
    }
  }

  async function loadJoinRequests() {
    if (!selectedGroup) return;
    setBusy("requests");
    try {
      const items = await listRemoteWhatsAppGroupJoinRequests(selectedGroup.groupId, session);
      setJoinRequests(items);
      notify(items.length ? "success" : "warning", items.length ? "Solicitudes actualizadas." : "No hay solicitudes de ingreso pendientes.");
    } catch (cause) {
      notify("danger", `No se pudieron cargar las solicitudes: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  async function resolveJoinRequest(phone: string, approve: boolean) {
    if (!selectedGroup) return;
    setBusy(`join-${phone}`);
    try {
      if (approve) await approveRemoteWhatsAppGroupJoinRequests(selectedGroup.groupId, [phone], session);
      else await rejectRemoteWhatsAppGroupJoinRequests(selectedGroup.groupId, [phone], session);
      setJoinRequests((items) => items.filter((item) => item.phone !== phone));
      notify("success", approve ? "Participante aprobado." : "Solicitud rechazada.");
    } catch (cause) {
      notify("danger", `No se pudo actualizar la solicitud: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  async function removeParticipant() {
    if (!selectedGroup || !participantPhone.trim()) return;
    const phone = participantPhone.replace(/[^0-9]/g, "");
    if (!phone) return;
    if (!window.confirm(`¿Quitar a +${phone} del grupo ${selectedGroup.subject}?`)) return;
    setBusy("remove");
    try {
      await removeRemoteWhatsAppGroupParticipants(selectedGroup.groupId, [phone], session);
      setParticipantPhone("");
      notify("success", "Participante retirado del grupo.");
    } catch (cause) {
      notify("danger", `No se pudo retirar al participante: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  async function sendMessage() {
    if (!selectedGroup || !messageBody.trim()) return;
    setBusy("message");
    try {
      await sendRemoteWhatsAppGroupMessage(selectedGroup.groupId, messageBody.trim(), session);
      setMessageBody("");
      notify("success", "Mensaje aceptado por WhatsApp y agregado a la conversación grupal.");
    } catch (cause) {
      notify("danger", `No se pudo enviar el mensaje: ${errorMessage(cause)}`);
    } finally {
      setBusy("");
    }
  }

  return (
    <section className="channel-section whatsapp-groups-section">
      <div className="channel-section-heading groups-heading">
        <span className="connection-icon groups"><Icon name="team" size={20} /></span>
        <div>
          <p className="section-kicker">GRUPOS OFICIALES</p>
          <h2>Coordinación con clientes y equipo</h2>
          <p>Conversa Smart crea y administra grupos mediante la API oficial cuando Meta habilita el número.</p>
        </div>
        <span className={eligible ? "state-chip active" : eligibility ? "state-chip error" : "state-chip pending"}>
          {eligible ? "Elegible" : eligibility ? "No habilitado" : "Sin comprobar"}
        </span>
      </div>

      {!activeConnections.length ? (
        <section className="card-panel groups-prerequisite">
          <span><Icon name="whatsapp" size={24} /></span>
          <div><strong>Primero activa un número de WhatsApp</strong><p>La comprobación de grupos usa las credenciales cifradas de una conexión activa.</p></div>
        </section>
      ) : (
        <>
          <section className="card-panel groups-readiness-card">
            <div className="groups-readiness-copy">
              <p className="section-kicker">PASO 1 · ELEGIBILIDAD</p>
              <h3>Comprueba el número antes de crear grupos</h3>
              <p>Esta acción realiza una consulta de sólo lectura a Meta. No crea grupos ni invita personas.</p>
            </div>
            <label className="field-label">
              <span>Número conectado</span>
              <select value={connectionId} onChange={(event) => setConnectionId(event.target.value)}>
                {activeConnections.map((connection) => (
                  <option key={connection.connectionId} value={connection.connectionId}>
                    {connection.label} · {connection.displayPhoneNumber || connection.phoneNumberId}
                  </option>
                ))}
              </select>
            </label>
            <div className="groups-readiness-actions">
              <button className="button secondary" disabled={Boolean(busy)} onClick={() => void checkEligibility()} type="button">
                {busy === "eligibility" ? "Comprobando…" : "Comprobar elegibilidad"}
              </button>
              <button className="button soft" disabled={Boolean(busy) || !eligible} onClick={() => void syncGroups()} type="button">
                <Icon name="download" size={16} /> {busy === "sync" ? "Sincronizando…" : "Sincronizar grupos"}
              </button>
            </div>
            {eligibility ? (
              <div className={eligibility.eligible ? "groups-eligibility success" : "groups-eligibility warning"}>
                <Icon name={eligibility.eligible ? "check" : "bell"} size={18} />
                <div><strong>{eligibility.eligible ? "Groups API disponible" : "Meta todavía no habilita este número"}</strong><span>{eligibility.eligible ? "Ya puedes crear y sincronizar grupos oficiales." : eligibilityMessage(eligibility.reason)}</span></div>
              </div>
            ) : null}
          </section>

          <div className="groups-admin-grid">
            <section className="card-panel groups-list-panel">
              <div className="panel-title-row">
                <div><p className="section-kicker">GRUPOS</p><h2>Grupos de {selectedConnection?.label ?? "esta conexión"}</h2></div>
                <span className="count-badge">{groups.filter((item) => item.status !== "resolved").length}</span>
              </div>
              <div className="groups-list">
                {groups.map((group) => (
                  <button className={selectedGroupId === group.groupId ? "group-row active" : "group-row"} key={group.groupId} onClick={() => setSelectedGroupId(group.groupId)} type="button">
                    <span className="group-avatar"><Icon name="team" size={18} /></span>
                    <span><strong>{group.subject}</strong><small>{group.participantCount ?? 0} participantes · {group.joinApprovalMode === "auto_approve" ? "ingreso automático" : "ingreso con aprobación"}</small></span>
                    <span className={`state-chip ${group.status}`}>{groupStatus(group.status)}</span>
                    <Icon name="arrow" size={16} />
                  </button>
                ))}
                {!groups.length ? (
                  <div className="friendly-empty groups-empty"><span><Icon name="team" size={24} /></span><strong>No hay grupos sincronizados</strong><p>Comprueba la elegibilidad y sincroniza o crea el primer grupo.</p></div>
                ) : null}
              </div>
            </section>

            <section className="card-panel groups-create-panel">
              <div className="panel-title-row"><div><p className="section-kicker">PASO 2</p><h2>Crear grupo operacional</h2></div></div>
              <p className="form-help">Meta enviará un evento de confirmación. Las personas ingresan mediante el enlace de invitación.</p>
              <div className="form-stack">
                <label className="field-label"><span>Nombre del grupo</span><input maxLength={100} value={createForm.subject} onChange={(event) => setCreateForm({ ...createForm, subject: event.target.value })} placeholder="Carozzi Concepción · Correctivo CCTV" /></label>
                <label className="field-label"><span>Descripción opcional</span><textarea value={createForm.description} onChange={(event) => setCreateForm({ ...createForm, description: event.target.value })} placeholder="Objetivo, responsables y reglas del grupo" /></label>
                <label className="field-label"><span>Ingreso de participantes</span><select value={createForm.joinApprovalMode} onChange={(event) => setCreateForm({ ...createForm, joinApprovalMode: event.target.value as typeof createForm.joinApprovalMode })}><option value="approval_required">Requiere aprobación</option><option value="auto_approve">Aprobación automática</option></select></label>
              </div>
              <button className="button primary wide" disabled={Boolean(busy) || !eligible || !createForm.subject.trim()} onClick={() => void createGroup()} type="button"><Icon name="plus" size={16} /> {busy === "create" ? "Solicitando a Meta…" : "Crear grupo oficial"}</button>
              {!eligible ? <p className="groups-disabled-copy">Disponible después de que Meta confirme la elegibilidad del número.</p> : null}
            </section>
          </div>

          {selectedGroup && !selectedGroup.groupId.startsWith("request-") && selectedGroup.status !== "resolved" ? (
            <section className="card-panel group-detail-panel">
              <div className="group-detail-header">
                <div><p className="section-kicker">PASO 3 · OPERAR</p><h2>{selectedGroup.subject}</h2><p>{selectedGroup.description || "Sin descripción"}</p></div>
                <span className={`state-chip ${selectedGroup.status}`}>{groupStatus(selectedGroup.status)}</span>
              </div>

              <div className="group-detail-grid">
                <article className="group-tool-card">
                  <div className="group-tool-title"><span><Icon name="settings" size={18} /></span><div><strong>Información del grupo</strong><small>Visible para sus participantes</small></div></div>
                  <label className="field-label"><span>Nombre</span><input value={editForm.subject} onChange={(event) => setEditForm({ ...editForm, subject: event.target.value })} /></label>
                  <label className="field-label"><span>Descripción</span><textarea value={editForm.description} onChange={(event) => setEditForm({ ...editForm, description: event.target.value })} /></label>
                  <button className="button secondary wide" disabled={Boolean(busy) || !editForm.subject.trim()} onClick={() => void saveGroup()} type="button">{busy === "edit" ? "Guardando…" : "Guardar cambios"}</button>
                </article>

                <article className="group-tool-card">
                  <div className="group-tool-title"><span><Icon name="external" size={18} /></span><div><strong>Enlace de invitación</strong><small>Comparte el acceso sin agregar personas en silencio</small></div></div>
                  {inviteLink ? <code className="group-invite-link">{inviteLink}</code> : <div className="group-tool-placeholder">Recupera el enlace actual cuando lo necesites.</div>}
                  <div className="group-tool-actions">
                    <button className="button soft compact" disabled={Boolean(busy)} onClick={() => void loadInviteLink(false)} type="button">Obtener enlace</button>
                    <button className="button secondary compact" disabled={Boolean(busy) || !inviteLink} onClick={() => void copyInviteLink()} type="button">Copiar</button>
                    <button className="text-link-button danger-link" disabled={Boolean(busy)} onClick={() => void loadInviteLink(true)} type="button">Restablecer</button>
                  </div>
                </article>

                <article className="group-tool-card">
                  <div className="group-tool-title"><span><Icon name="team" size={18} /></span><div><strong>Solicitudes de ingreso</strong><small>Aprobar o rechazar participantes</small></div></div>
                  <button className="button soft compact" disabled={Boolean(busy)} onClick={() => void loadJoinRequests()} type="button">{busy === "requests" ? "Consultando…" : "Actualizar solicitudes"}</button>
                  <div className="join-request-list">
                    {joinRequests.map((request) => (
                      <div key={request.phone}><span><strong>+{request.phone}</strong><small>{request.requestedAt || "Solicitud pendiente"}</small></span><div><button disabled={Boolean(busy)} onClick={() => void resolveJoinRequest(request.phone, true)} type="button">Aprobar</button><button className="reject" disabled={Boolean(busy)} onClick={() => void resolveJoinRequest(request.phone, false)} type="button">Rechazar</button></div></div>
                    ))}
                    {!joinRequests.length ? <p>No hay solicitudes cargadas.</p> : null}
                  </div>
                </article>

                <article className="group-tool-card">
                  <div className="group-tool-title"><span><Icon name="send" size={18} /></span><div><strong>Enviar mensaje</strong><small>Queda guardado en la conversación grupal</small></div></div>
                  <textarea className="group-message-box" value={messageBody} onChange={(event) => setMessageBody(event.target.value)} placeholder="Escribe un mensaje para el grupo…" />
                  <button className="button primary wide" disabled={Boolean(busy) || !messageBody.trim()} onClick={() => void sendMessage()} type="button"><Icon name="send" size={15} /> {busy === "message" ? "Enviando…" : "Enviar al grupo"}</button>
                </article>

                <article className="group-tool-card group-danger-card">
                  <div className="group-tool-title"><span><Icon name="team" size={18} /></span><div><strong>Retirar participante</strong><small>No elimina su historial previo</small></div></div>
                  <label className="field-label"><span>Teléfono con código de país</span><input value={participantPhone} onChange={(event) => setParticipantPhone(event.target.value)} placeholder="+56 9..." /></label>
                  <button className="button secondary wide" disabled={Boolean(busy) || !participantPhone.trim()} onClick={() => void removeParticipant()} type="button">{busy === "remove" ? "Retirando…" : "Retirar del grupo"}</button>
                </article>
              </div>
            </section>
          ) : null}
        </>
      )}
    </section>
  );
}

function groupStatus(value: string) {
  if (value === "active" || value === "synced") return "Activo";
  if (value === "creating") return "Creando";
  if (value === "resolved") return "Reemplazado";
  if (value === "paused") return "Pausado";
  return value || "Pendiente";
}

function eligibilityMessage(reason?: string) {
  const labels: Record<string, string> = {
    WHATSAPP_CONNECTION_NOT_ACTIVE: "Activa y valida primero esta conexión de WhatsApp.",
    META_GROUPS_NOT_AVAILABLE: "Meta no expone Groups API para este número o aplicación.",
    META_GROUPS_PERMISSION_REQUIRED: "El token o la aplicación todavía no tienen autorización para Groups API.",
    META_GROUPS_OBA_REQUIRED: "La cuenta aún no cumple la elegibilidad empresarial exigida por Meta.",
    META_GROUPS_COEXISTENCE_NOT_SUPPORTED: "Este número usa una modalidad incompatible con grupos oficiales.",
    META_ACCESS_TOKEN_INVALID: "El token de Meta venció o dejó de ser válido.",
  };
  return labels[reason ?? ""] ?? "Meta rechazó la consulta de grupos. Revisa la elegibilidad y los permisos del número.";
}

function errorMessage(cause: unknown) {
  const raw = cause instanceof Error ? cause.message : "ERROR_DESCONOCIDO";
  const [code, providerCode] = raw.split("::");
  const label = eligibilityMessage(code);
  return providerCode ? `${label} Código Meta ${providerCode}.` : label;
}
